import {
  type AnimationOptionsObject,
  type Point,
  type PointDropEventObject,
  type PointOptionsObject,
  type SeriesOptionsType,
} from "highcharts"
import { countBy } from "lodash"

import {
  MixedChartData,
  ScatterChartData,
  StackedBarChartData,
  WaterfallChartData,
  type CategoryChartSeries,
  type DateChartSeries,
  type HeatMapData,
  type PieChartData,
} from "../SuperChart"

export function generateHighchartsPieSeries(
  data: PieChartData[],
  label: string | undefined,
): SeriesOptionsType[] {
  return [
    {
      type: "pie",
      name: label,
      borderRadius: 8,
      borderWidth: 4, // Adds a gap between the items
      innerSize: "75%",
      data: data.map((entry) => ({
        name: entry.name,
        color: entry.color,
        custom: entry.custom,
        y: entry.disabled ? 0 : entry.data, // Doing this animates the transition when disabling a slice
      })),
    },
  ]
}

export function generateHighchartsHeatmapSeries(data: HeatMapData[]): SeriesOptionsType[] {
  return [
    {
      type: "heatmap",
      data: data as any, // Highcharts expects [x, y, value] format for heatmap data
      dataLabels: {
        enabled: true,
        style: {
          textOutline: "none",
          fontWeight: "500",
          fontSize: "12px",
        },
        formatter: function (this: PointOptionsObject) {
          // A null point is "no value", not zero — labelling it 0% would assert a measurement.
          return this.value == null ? "" : Math.round(this.value) + "%"
        },
      },
    },
  ]
}

export function generateWaterfallHighchartsSeries(
  series: WaterfallChartData[],
): SeriesOptionsType[] {
  return series.map((series) => ({
    type: "waterfall",
    name: series.name,
    color: "rgb(94, 233, 181)",
    negativeColor: "rgb(255, 162, 162)",
    data: series.data,
    lineWidth: 2,
    marker: { enabled: false },
    zIndex: 2,
  }))
}

// The drop event's new Y lives on e.newPoint across Highcharts versions; fall back to the point itself.
type DropEvt = { newPoint?: { newValues?: { y?: number }; y?: number } }
const droppedY = (point: Point, e: PointDropEventObject): number => {
  const np = (e as unknown as DropEvt).newPoint
  return Number(np?.newValues?.y ?? np?.y ?? point.y ?? 0)
}

export function generateDateHighchartsSeries(
  series: DateChartSeries[],
  animation: boolean | AnimationOptionsObject = false,
  // Ring color for `pinned` point handles — SuperChart passes its theme background-tone.
  pinRingColor?: string,
  /** Chart surface color, drawn as a border so touching stack segments keep a visible seam. */
  stackSeamColor?: string,
): SeriesOptionsType[] {
  const stackSizes = countBy(
    series.filter((entry) => entry.type === "column" && entry.stack !== undefined),
    "stack",
  )

  return series.map((entry) => {
    const baseColor = entry.colors?.[0] ?? "rgb(169, 139, 250)"
    const patternFill = entry.pattern
      ? {
          pattern: {
            path: { d: entry.pattern.path, strokeWidth: entry.pattern.strokeWidth },
            width: entry.pattern.width,
            height: entry.pattern.height,
            color: baseColor,
            backgroundColor: "transparent",
          },
        }
      : undefined
    // When a pattern is provided, apply it per-point so the bars get the pattern
    // while the series-level `color` (used for the legend swatch) stays solid.
    const data = entry.disabled
      ? []
      : entry.data?.map((d) => {
          const pointColor = patternFill ?? d.color
          const yValue = entry.type === "column" && d.value === 0 ? null : d.value
          // fillColor is left to default to the series color, so the handle matches its line.
          const marker = d.pinned
            ? { enabled: true, radius: 5, lineWidth: 2, lineColor: pinRingColor }
            : undefined
          return pointColor !== undefined || d.tooltipValue !== undefined || marker !== undefined
            ? {
                x: d.milliseconds,
                y: yValue,
                ...(pointColor !== undefined ? { color: pointColor } : {}),
                ...(marker !== undefined ? { marker } : {}),
                ...(d.tooltipValue !== undefined
                  ? { custom: { tooltipValue: d.tooltipValue } }
                  : {}),
              }
            : [d.milliseconds, yValue]
        })
    const seriesOptions = {
      type: entry.type,
      name: entry.displayName ?? entry.name,
      data,
      lineWidth: entry.lineWidth ?? 2,
      dashStyle: entry.dashStyle ?? "Solid",
      zIndex: 2,
      color: baseColor,
      marker: { enabled: false },
      animation,
      threshold: entry.threshold ?? 0,
      yAxis: entry.yAxis ?? 0,
      stack: entry.stack,
      stacking: entry.stacking,
      grouping: entry.grouping,
      custom: entry.custom,
      ...(entry.draggableY
        ? {
            cursor: "ns-resize",
            dragDrop: {
              draggableX: false,
              draggableY: true,
              dragMinY: entry.draggableY.min,
              dragMaxY: entry.draggableY.max,
            },
          }
        : {}),
      ...(entry.onPointDrop || entry.onPointClick
        ? {
            point: {
              events: {
                ...(entry.onPointDrop
                  ? {
                      drop: function (this: Point, e: PointDropEventObject) {
                        entry.onPointDrop!(Number(this.x), droppedY(this, e))
                      },
                    }
                  : {}),
                ...(entry.onPointClick
                  ? {
                      click: function (this: Point) {
                        entry.onPointClick!(Number(this.x))
                      },
                    }
                  : {}),
              },
            },
          }
        : {}),
    } as SeriesOptionsType

    if (patternFill && entry.type === "column") {
      // to make sure it gets a solid border when it is dashed.
      Object.assign(seriesOptions, {
        borderColor: baseColor,
        borderWidth: 1,
        dashStyle: "Solid",
        custom: { ...entry.custom, swatchDashStyle: entry.dashStyle ?? "Solid", hatched: true },
      })
    }

    // Every segment of a stack takes the same border in the chart surface color: neighbours
    // sharing a hue stay separated, and no segment reads as wider or more outlined than the
    // one below it. This replaces the hatched column's own edge, whose fill already marks it.
    if (
      entry.stack !== undefined &&
      entry.type === "column" &&
      stackSeamColor &&
      (stackSizes[entry.stack] ?? 0) > 1
    ) {
      Object.assign(seriesOptions, { borderColor: stackSeamColor, borderWidth: 1 })
    }

    if (entry.type === "areaspline") {
      // @ts-expect-error - Type error
      seriesOptions.fillColor = {
        linearGradient: {
          x1: 0,
          y1: 0,
          x2: 0,
          y2: 1,
        },
        stops: generateAreaColorStops((entry.colors?.[0] ?? "rgb(169, 139, 250)") as string),
      }
    }

    if (entry.showNegativeColor) {
      // @ts-expect-error - Type error
      seriesOptions.negativeFillColor = {
        linearGradient: {
          x1: 0,
          y1: 0,
          x2: 0,
          y2: 1,
        },
        stops: generateAreaColorStops("rgb(248, 38, 63)"),
      }
    }

    return seriesOptions
  })
}

export function generateStackedColumnHighchartsSeries(
  series: StackedBarChartData[],
  labelStyle?: { color: string; fontSize: string },
): SeriesOptionsType[] {
  return series.map((entry) => {
    const { pointLabels } = entry
    return {
      type: entry.type,
      name: entry.name,
      data: entry.data,
      lineWidth: 2,
      color: entry.color,
      marker: { enabled: false },
      stacking: "normal" as const,
      zIndex: 2,
      // Callouts go through ONE series-level dataLabels object with a formatter that returns
      // a label only for the points that have one, rather than per-point dataLabels. Per-point
      // labels make Highcharts build a data-label group from the SERIES options, which
      // plotOptions.column leaves undefined unless showDataLabels is on — it then throws in
      // initDataLabelsGroup. Returning undefined from the formatter suppresses that point.
      ...(pointLabels
        ? {
            dataLabels: {
              enabled: true,
              crop: false,
              overflow: "allow" as const,
              inside: false,
              formatter: function (this: Point): string | undefined {
                return pointLabels[this.index] ?? undefined
              },
              style: {
                fontSize: labelStyle?.fontSize ?? "11px",
                fontWeight: "600",
                color: labelStyle?.color,
                textOutline: "none",
              },
            },
          }
        : {}),
    }
  })
}

export function generateHighchartsCategorySeries(
  series: CategoryChartSeries[],
  animation: boolean | AnimationOptionsObject = false,
): SeriesOptionsType[] {
  return series.map((series) => {
    const seriesOptions = {
      type: series.type, // Use the chart type defined in the series
      name: series.displayName ?? series.name,
      data: series.disabled
        ? []
        : series.data.map((entry) => ({
            name: entry.category,
            y: entry.value,
            color: entry.color ?? series.colors?.[0],
          })),
      lineWidth: series.lineWidth ?? 2,
      dashStyle: series.dashStyle ?? "Solid",
      color: series.colors?.[0] ?? "rgb(169, 139, 250)",
      animation,
      marker: { enabled: false },
      zIndex: 2,
    }

    if (series.type === "areaspline") {
      // @ts-expect-error - Type error
      seriesOptions.fillColor = {
        linearGradient: {
          x1: 0,
          y1: 0,
          x2: 0,
          y2: 1,
        },
        stops: generateAreaColorStops((series.colors?.[0] ?? "rgb(169, 139, 250)") as string),
      }
    }

    return seriesOptions
  })
}

export function generateHighchartsMixedSeries(
  series: MixedChartData[],
  fallbackColor: string,
): SeriesOptionsType[] {
  //@ts-expect-error
  return series.map((s) => ({
    lineWidth: 2,
    color: fallbackColor,
    ...s,
    ...(s.disabled ? { data: [] } : {}),
  }))
}

// Accepts #hex, rgb() or rgba() and emits low-opacity gradient stops from the base RGB.
// Theme tokens resolve to hex and faded series colours arrive as rgba(); both must yield a
// valid fill rather than a malformed 5-arg rgba (which renders black) or an un-faded hex.
function colorToRgbTriple(color: string): [number, number, number] {
  if (color.startsWith("#")) {
    let hex = color.slice(1)
    if (hex.length === 3)
      hex = hex
        .split("")
        .map((c) => c + c)
        .join("")
    return [
      parseInt(hex.slice(0, 2), 16),
      parseInt(hex.slice(2, 4), 16),
      parseInt(hex.slice(4, 6), 16),
    ]
  }
  const parts = color.match(/-?\d+(\.\d+)?/g)
  return [Number(parts?.[0] ?? 0), Number(parts?.[1] ?? 0), Number(parts?.[2] ?? 0)]
}

export function generateAreaColorStops(color: string): (string | number)[][] {
  const [r, g, b] = colorToRgbTriple(color)
  return [
    [0.0, `rgba(${r}, ${g}, ${b}, 0.2)`],
    [0.15, `rgba(${r}, ${g}, ${b}, 0.1)`],
    [0.3, `rgba(${r}, ${g}, ${b}, 0.05)`],
    [1, `rgba(${r}, ${g}, ${b}, 0.02)`],
  ]
}

export function calculateLinearRegression(data: { x: number; y: number }[]): {
  slope: number
  intercept: number
} {
  const n = data.length
  if (n === 0) return { slope: 0, intercept: 0 }

  let sumX = 0
  let sumY = 0
  let sumXY = 0
  let sumX2 = 0

  for (const point of data) {
    sumX += point.x
    sumY += point.y
    sumXY += point.x * point.y
    sumX2 += point.x * point.x
  }

  const denominator = n * sumX2 - sumX * sumX
  if (denominator === 0) return { slope: 0, intercept: sumY / n }

  const slope = (n * sumXY - sumX * sumY) / denominator
  const intercept = (sumY - slope * sumX) / n

  return { slope, intercept }
}

export interface ScatterChartOptions {
  markerRadius?: number
  regressionLineColor?: string
}

export function generateScatterHighchartsSeries(
  series: ScatterChartData[],
  showRegression: boolean,
  fallbackColor: string,
  options?: ScatterChartOptions,
): SeriesOptionsType[] {
  const result: SeriesOptionsType[] = []
  const markerRadius = options?.markerRadius ?? 5
  const regressionLineColor = options?.regressionLineColor

  for (const s of series) {
    if (s.disabled) {
      result.push({
        type: "scatter",
        name: s.name,
        data: [],
        color: s.color ?? fallbackColor,
        marker: { radius: markerRadius },
        zIndex: 2,
      })
      continue
    }

    result.push({
      type: "scatter",
      name: s.name,
      data: s.data.map((point) => ({
        x: point.x,
        y: point.y,
        name: point.name,
        custom: {
          variantId: point.variantId,
          variantName: point.variantName,
          productName: point.productName,
          imageUrl: point.imageUrl,
        },
      })),
      color: s.color ?? fallbackColor,
      marker: { radius: markerRadius },
      zIndex: 2,
    })

    if (showRegression && s.data.length >= 2) {
      let lineData: [number, number][]

      if (s.regressionLine && s.regressionLine.length >= 2) {
        lineData = s.regressionLine.map((p) => [p.x, p.y])
      } else {
        const { slope, intercept } = calculateLinearRegression(s.data)
        const xValues = s.data.map((p) => p.x)
        const minX = Math.min(...xValues)
        const maxX = Math.max(...xValues)
        lineData = [
          [minX, slope * minX + intercept],
          [maxX, slope * maxX + intercept],
        ]
      }

      result.push({
        type: "line",
        name: `${s.name} (regression)`,
        data: lineData,
        color: regressionLineColor ?? s.color ?? fallbackColor,
        lineWidth: 4,
        dashStyle: "Dash",
        marker: { enabled: false },
        enableMouseTracking: false,
        showInLegend: false,
        zIndex: 3,
      })
    }
  }

  return result
}
